'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import Link from 'next/link'
import Navbar from '@/components/Navbar'
import { AgentProfile } from '@/lib/agent-profiles'
import { createBrowserClient } from '@supabase/ssr'

function getSupabase() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
  )
}

interface Message { role: 'user' | 'assistant'; text: string; time: string }
interface Review { id: string; agent_slug: string; reviewer_name: string; reviewer_role: string; rating: number; review_text: string; created_at: string; approved: boolean }

function formatTimer(s: number) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
}
function timeAgo(dateStr: string) {
  const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
  return d < 1 ? 'Today' : d < 30 ? `${d}d ago` : `${Math.floor(d/30)}mo ago`
}
function StarRating({ rating, size='sm' }: { rating: number; size?: 'sm'|'lg' }) {
  return <span className={size==='lg'?'text-xl':'text-sm'}>{[1,2,3,4,5].map(i=><span key={i} className={i<=rating?'text-amber-400':'text-gray-300'}>★</span>)}</span>
}



// ─── Signup Modal ─────────────────────────────────────────────────────────────
function SignupModal({ onSuccess, onClose, agentName }: { onSuccess: () => void; onClose: () => void; agentName: string }) {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([null,null,null,null,null,null])

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(s => s - 1), 1000)
      return () => clearTimeout(t)
    }
  }, [resendCooldown])

  const sendOtp = async () => {
    setError(''); setLoading(true)
    try {
      const chk = await fetch('/api/agent-trial/validate-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) })
      const { valid, reason } = await chk.json()
      if (!valid) { setError(reason || 'Please use a real email address.'); setLoading(false); return }
    } catch { /* fail open */ }
    const sb = getSupabase()
    const { error } = await sb.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: true } })
    setLoading(false)
    if (error) { setError(error.message); return }
    setStep('otp'); setResendCooldown(60)
    setTimeout(() => otpRefs.current[0]?.focus(), 100)
  }

  const handleOtpKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value
    if (e.key === 'Backspace' && !val && i > 0) otpRefs.current[i - 1]?.focus()
  }

  const handleOtpInput = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '')
    // Autofill/paste: distribute all digits across boxes
    if (raw.length > 1) {
      const chars = raw.slice(0, 6).split('')
      chars.forEach((ch, idx) => {
        if (otpRefs.current[idx]) otpRefs.current[idx]!.value = ch
      })
      const full = chars.join('')
      setOtp(full)
      otpRefs.current[Math.min(chars.length - 1, 5)]?.focus()
      if (full.length === 6) verifyOtp(full)
      return
    }
    const val = raw.slice(-1)
    e.target.value = val
    const digits = otpRefs.current.map(r => r?.value || '')
    digits[i] = val
    setOtp(digits.join(''))
    if (val && i < 5) otpRefs.current[i + 1]?.focus()
    if (digits.every(d => d) && digits.join('').length === 6) {
      verifyOtp(digits.join(''))
    }
  }

  const verifyOtp = async (code?: string) => {
    const token = code || otp
    if (token.length !== 6) return
    setError(''); setLoading(true)
    const sb = getSupabase()
    const { error } = await sb.auth.verifyOtp({ email: email.trim(), token, type: 'email' })
    setLoading(false)
    if (error) { setError('Invalid code. Please try again.'); return }
    onSuccess()
  }

  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" aria-label="Close"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#0f0f0f] flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="font-[Cormorant_Garamond] text-2xl font-bold text-gray-900">
            {step === 'email' ? 'Start your free trial' : 'Check your email'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === 'email'
              ? `Get 24 hours free with AI ${agentName}`
              : `We sent a 6-digit code to ${email}`}
          </p>
        </div>

        {step === 'email' ? (
          <>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && email.trim() && sendOtp()}
                placeholder="you@example.com"
                autoFocus
                className="w-full text-sm border border-[#E5E5E0] rounded-xl px-4 py-3 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
              />
            </div>
            {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
            <button
              onClick={sendOtp}
              disabled={loading || !email.trim()}
              className="w-full bg-[#0f0f0f] hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors text-sm"
            >
              {loading ? 'Sending code…' : 'Send verification code →'}
            </button>
            <p className="text-xs text-gray-400 text-center mt-4">
              Any email works. No credit card required.
            </p>
          </>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3 text-center">Enter 6-digit code</label>
              <div className="flex justify-center gap-2">
                {[0,1,2,3,4,5].map(i => (
                  <input
                    key={i}
                    ref={el => { otpRefs.current[i] = el }}
                    type="text"
                    inputMode="numeric"
                    maxLength={i === 0 ? 6 : 1}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    onKeyDown={e => handleOtpKey(i, e)}
                    onChange={e => handleOtpInput(i, e)}
                    className="w-10 h-12 text-center text-lg font-bold border-2 border-[#E5E5E0] rounded-xl focus:outline-none focus:border-gray-900 transition-colors"
                  />
                ))}
              </div>
            </div>
            {error && <p className="text-xs text-red-600 mb-3 text-center">{error}</p>}
            <button
              onClick={() => verifyOtp()}
              disabled={loading || otp.length !== 6}
              className="w-full bg-[#0f0f0f] hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors text-sm mb-3"
            >
              {loading ? 'Verifying…' : 'Verify & start trial →'}
            </button>
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-xs text-gray-400">Resend in {resendCooldown}s</p>
              ) : (
                <button onClick={sendOtp} className="text-xs text-gray-600 hover:text-gray-900 underline">
                  Resend code
                </button>
              )}
              <button onClick={() => { setStep('email'); setOtp(''); setError('') }} className="block w-full text-xs text-gray-400 hover:text-gray-600 mt-1">
                ← Use different email
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  , document.body)
}

function ActivityIcon({ icon }: { icon: string }) {
  const cls = "w-4 h-4 text-gray-600"
  const map: Record<string, React.ReactElement> = {
    // Check / done
    '✅': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>,
    '✓':  <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>,
    // Document / clipboard
    '📋': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>,
    '📄': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    '📝': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
    // Calendar
    '📅': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    '🗓️': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    // Mail / message
    '✉️': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    '📬': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    // Chart / analytics
    '📊': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    '📈': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
    '📉': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>,
    // Search / magnifier
    '🔍': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    '🔮': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    // People / users
    '🤝': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    '👤': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
    // Money / finance
    '💰': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    '💎': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3l3 7 3-7M9 3H3m18 0h-6M3 10l9 11 9-11" /></svg>,
    // Alert / warning
    '⚠️': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
    '🚨': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
    '🔥': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" /></svg>,
    // Security / lock
    '🔐': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
    '🛡️': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    // Tech / compute
    '🖥️': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    '🚀': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 3l14 9-14 9V3z" /></svg>,
    '🔧': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    '🔄': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
    // Target / goal
    '🎯': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10" strokeWidth={2}/><circle cx="12" cy="12" r="6" strokeWidth={2}/><circle cx="12" cy="12" r="2" strokeWidth={2}/></svg>,
    // Truck / logistics
    '🚚': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>,
    // Chat / support
    '💬': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
    '🎫': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>,
    // Globe / world
    '🌍': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    // Building / institution
    '🏛️': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>,
    // Sparkles / celebration
    '🎉': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>,
    // Shopping / cart
    '🛒': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
  }
  const svg = map[icon]
  if (svg) return svg
  // fallback: lightning bolt
  return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  )
}

function ChatPanel({ profile }: { profile: AgentProfile }) {
  const sessionId = useRef(Math.random().toString(36).slice(2))
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    text: `Hi! I'm your AI ${profile.role} — try me out for free.\n\nAsk me anything, or upload a document and I'll analyze it instantly.`,
    time: 'Just now',
  }])
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const [chipsSent, setChipsSent] = useState(false)
  const [timerSec, setTimerSec] = useState(86400)
  const [trialExpired, setTrialExpired] = useState(false)
  const [trialLoaded, setTrialLoaded] = useState(false)
  const [isAuthed, setIsAuthed] = useState(false)
  const [showSignup, setShowSignup] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState<{name:string;text:string;type:string}|null>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachRef = useRef<HTMLDivElement>(null)

  // Check auth state
  useEffect(() => {
    const sb = getSupabase()
    sb.auth.getUser().then(({ data }) => setIsAuthed(!!data.user))
    const { data: { subscription } } = sb.auth.onAuthStateChange((_e, session) => setIsAuthed(!!session))
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    fetch('/api/agent-trial/session', {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ agent_slug: profile.slug }),
    }).then(r=>r.json()).then(d=>{
      setTimerSec(d.remainingSec ?? 86400)
      setTrialExpired(d.expired ?? false)
      setTrialLoaded(true)
    }).catch(()=>setTrialLoaded(true))
  }, [profile.slug, isAuthed])

  useEffect(() => {
    const iv = setInterval(()=>setTimerSec(s=>{
      const n=Math.max(0,s-1); if(n===0) setTrialExpired(true); return n
    }),1000)
    return ()=>clearInterval(iv)
  }, [])

  useEffect(() => {
    if(isFirstRender.current){isFirstRender.current=false;return}
    if(messagesContainerRef.current){
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      // On mobile: keep the chat panel in view (prevent page scrolling away)
      if (window.innerWidth < 768) {
        const panel = messagesContainerRef.current.closest('[data-chat-panel]')
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [messages, typing])

  useEffect(() => {
    const h=(e:MouseEvent)=>{ if(attachRef.current&&!attachRef.current.contains(e.target as Node)) setAttachOpen(false) }
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h)
  }, [])

  const sendMessage = useCallback(async (text: string, file?: {name:string;text:string}) => {
    if(!text.trim()&&!file) return
    if(!isAuthed) { setShowSignup(true); return }
    setChipsSent(true)
    const now = new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})
    setMessages(prev=>[...prev,{role:'user',text:file?(text||`Analyzing: ${file.name}`):text,time:now}])
    setInput(''); setPendingFile(null); setTyping(true)
    try {
      const body: Record<string,string> = { message: text||`Analyze this file: ${file?.name}`, sessionId: sessionId.current, agentSlug: profile.slug }
      if(file){body.fileText=file.text; body.fileName=file.name}
      const res = await fetch('/api/agent-trial/chat',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})
      const data = await res.json()
      setMessages(prev=>[...prev,{role:'assistant',text:data.reply||"I'm here to help!",time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}])
    } catch {
      setMessages(prev=>[...prev,{role:'assistant',text:"Could you rephrase that?",time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}])
    } finally { setTyping(false) }
  }, [profile.slug, isAuthed])

  const handleSend = () => sendMessage(input)
  const handleKeyDown = (e:React.KeyboardEvent) => { if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleSend()} }

  const handleFileType = (type: string) => {
    setAttachOpen(false)
    if(fileInputRef.current){
      const accept:Record<string,string> = {PDF:'.pdf',PowerPoint:'.ppt,.pptx',Excel:'.xls,.xlsx,.csv',Word:'.doc,.docx,.txt'}
      fileInputRef.current.accept = accept[type]||'*'
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (e:React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if(!file) return
    const isText = file.type.includes('text')||file.name.endsWith('.csv')||file.name.endsWith('.txt')
    const text = isText ? await file.text() : `[Binary file — ${file.name}]`
    setPendingFile({name:file.name,text,type:file.type})
    e.target.value=''
  }

  const chips = [`How do you handle ${profile.role.toLowerCase()} tasks?`, 'What languages do you speak?', 'Upload a document to analyze']

  return (
    <>
    {showSignup && <SignupModal agentName={profile.role} onSuccess={() => { setIsAuthed(true); setShowSignup(false) }} onClose={() => setShowSignup(false)} />}
    <div data-chat-panel className="bg-white border border-[#E5E5E0] rounded-2xl shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a0533] to-[#2d0a5e] p-4 rounded-t-2xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center overflow-hidden ring-2 ring-violet-400/40">
              <Image src="/eve-bot.png" alt="Eve" width={40} height={40} className="w-full h-full object-cover" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#22c55e] rounded-full ring-2 ring-[#1a0533]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold leading-tight truncate">AI {profile.role}</p>
            <p className="text-xs text-gray-400">powered by Claude · <span className="text-[#22c55e]">Online</span></p>
          </div>
          <span className="flex-shrink-0 text-[10px] font-extrabold tracking-widest uppercase bg-gradient-to-r from-violet-400 to-fuchsia-400 text-transparent bg-clip-text border border-violet-500/50 px-2.5 py-1 rounded-full whitespace-nowrap">
            Free Trial
          </span>
        </div>
        <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border-2 border-violet-400/60 flex items-center justify-center">
              <svg className="w-3 h-3 text-violet-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">Trial window</p>
              <p className="text-white font-mono text-sm font-bold tracking-wider leading-none">{trialLoaded ? formatTimer(timerSec) : '--:--:--'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">Status</p>
            {trialExpired ? <p className="text-red-400 text-xs font-semibold leading-none">Expired</p> : <p className="text-[#22c55e] text-xs font-semibold leading-none">Active</p>}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="h-[220px] md:h-[260px] overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.map((msg,i)=>(
          <div key={i} className={`flex ${msg.role==='user'?'justify-end':'justify-start'}`}>
            {msg.role==='assistant' && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-purple-800 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5 overflow-hidden">
                <Image src="/eve-bot.png" alt="Eve" width={28} height={28} className="w-full h-full object-cover" />
              </div>
            )}
            <div className={`max-w-[75%] ${msg.role==='user'?'order-1':''}`}>
              <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${msg.role==='user'?'bg-[#7c3aed] text-white rounded-tr-sm':'bg-white text-gray-800 border border-[#E5E5E0] rounded-tl-sm shadow-sm'}`}>
                {msg.text}
              </div>
              <p className="text-[10px] text-gray-400 mt-1 px-1">{msg.time}</p>
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-purple-800 flex items-center justify-center overflow-hidden flex-shrink-0 mt-0.5">
              <Image src="/eve-bot.png" alt="Eve" width={28} height={28} className="w-full h-full object-cover" />
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <p className="text-xs text-gray-400 mb-1.5 font-medium">Working on it…</p>
              <span className="flex gap-1.5">
                {[0,150,300].map(d=><span key={d} className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{animationDelay:`${d}ms`}} />)}
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {!chipsSent && isAuthed && (
        <div className="px-4 pb-2 bg-gray-50 flex flex-wrap gap-1.5">
          {chips.map(chip=>(
            <button key={chip} onClick={()=>sendMessage(chip)} className="text-xs bg-white border border-[#E5E5E0] hover:border-violet-400 hover:text-violet-700 text-gray-600 px-3 py-1.5 rounded-full transition-colors">
              {chip}
            </button>
          ))}
        </div>
      )}

      {trialExpired && (
        <div className="mx-3 mb-3 mt-1 bg-red-50 border border-red-200 rounded-xl p-3 text-center">
          <p className="text-sm font-semibold text-red-700 mb-1">Your trial has ended</p>
          <p className="text-xs text-red-500 mb-2">Hire this agent to continue chatting</p>
          <a href={`/login?redirect=/ai/${profile.slug}`} className="inline-block bg-[#7c3aed] text-white text-xs font-bold px-4 py-1.5 rounded-full hover:bg-violet-700 transition-colors">Hire Now →</a>
        </div>
      )}

      <div className="p-3 border-t border-[#E5E5E0] bg-white">
        <div className="flex items-center gap-2">
          <input type="text" value={input}
            onChange={e=>!trialExpired&&setInput(e.target.value)}
            onFocus={() => { if(!isAuthed) setShowSignup(true) }}
            onKeyDown={handleKeyDown}
            placeholder={trialExpired ? "Trial ended — hire to continue" : !isAuthed ? "Sign up free to start chatting…" : profile.chatPlaceholder}
            disabled={trialExpired}
            className="flex-1 text-sm border border-[#E5E5E0] rounded-full px-4 py-2 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            readOnly={!isAuthed && !trialExpired}
          />
          <button onClick={handleSend} disabled={!input.trim()||trialExpired}
            className="w-8 h-8 rounded-full bg-[#7c3aed] hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center transition-colors flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
    </>
  )
}

function ReviewsSection({ slug }: { slug: string }) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [formRating, setFormRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [formName, setFormName] = useState('')
  const [formRole, setFormRole] = useState('')
  const [formText, setFormText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetch(`/api/agent-trial/reviews?slug=${slug}`).then(r=>r.json()).then(d=>{setReviews(d.reviews||[]);setLoading(false)}).catch(()=>setLoading(false))
  }, [slug])

  const avg = reviews.length ? (reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1) : '5.0'
  const counts = [5,4,3,2,1].map(star=>({star,count:reviews.filter(r=>r.rating===star).length}))
  const maxCount = Math.max(...counts.map(r=>r.count),1)

  const handleSubmit = async () => {
    setError('')
    if(!formRating) return setError('Please select a rating.')
    if(!formName.trim()) return setError('Please enter your name.')
    if(!formText.trim()) return setError('Please write a review.')
    setSubmitting(true)
    try {
      const res = await fetch('/api/agent-trial/reviews',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({agent_slug:slug,reviewer_name:formName,reviewer_role:formRole,rating:formRating,review_text:formText})})
      const data = await res.json()
      if(!res.ok){setError(data.error||'Failed.');return}
      setReviews(prev=>[data.review,...prev]); setSubmitted(true)
      setFormRating(0);setFormName('');setFormRole('');setFormText('')
      setTimeout(()=>setSubmitted(false),4000)
    } catch { setError('Network error.') } finally { setSubmitting(false) }
  }

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
      <h2 className="font-[Cormorant_Garamond] text-2xl font-semibold text-gray-900 mb-5">Reviews <span className="text-gray-400 text-lg font-normal">{reviews.length} total</span></h2>
      <div className="flex items-start gap-6 mb-6">
        <div className="text-center">
          <p className="text-5xl font-bold text-gray-900">{avg}</p>
          <StarRating rating={Math.round(Number(avg))} size="lg" />
          <p className="text-sm text-gray-500 mt-1">{reviews.length} reviews</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {counts.map(({star,count})=>(
            <div key={star} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-3">{star}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className="h-2 bg-amber-400 rounded-full transition-all" style={{width:`${(count/maxCount)*100}%`}} />
              </div>
              <span className="text-xs text-gray-400 w-5 text-right">{count}</span>
            </div>
          ))}
        </div>
      </div>
      {loading ? <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="animate-pulse h-12 bg-gray-100 rounded" />)}</div> : (
        <div className="space-y-5 mb-8">
          {reviews.map(r=>(
            <div key={r.id} className="border-b border-[#F0F0ED] pb-5 last:border-0">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-100 to-purple-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-violet-700 font-semibold text-sm">{r.reviewer_name.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm">{r.reviewer_name}</p>
                    <p className="text-xs text-gray-400">{r.reviewer_role} · {timeAgo(r.created_at)}</p>
                  </div>
                  <StarRating rating={r.rating} />
                  <p className="text-sm text-gray-700 mt-1.5 leading-relaxed">{r.review_text}</p>
                </div>
              </div>
            </div>
          ))}
          {reviews.length===0 && <p className="text-gray-400 text-sm text-center py-4">No reviews yet — be the first!</p>}
        </div>
      )}
      <div className="border-t border-[#F0F0ED] pt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Leave a review</h3>
        {submitted && <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm mb-4">✓ Review submitted! Thank you.</div>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">{error}</div>}
        <div className="flex gap-1 mb-4">
          {[1,2,3,4,5].map(star=>(
            <button key={star} onMouseEnter={()=>setHoverRating(star)} onMouseLeave={()=>setHoverRating(0)} onClick={()=>setFormRating(star)}
              className={`text-2xl transition-colors ${star<=(hoverRating||formRating)?'text-amber-400':'text-gray-200 hover:text-amber-200'}`}>★</button>
          ))}
        </div>
        <div className="space-y-3">
          <input type="text" placeholder="Your name" value={formName} onChange={e=>setFormName(e.target.value)} className="w-full text-sm border border-[#E5E5E0] rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-100" />
          <input type="text" placeholder="Your role (optional)" value={formRole} onChange={e=>setFormRole(e.target.value)} className="w-full text-sm border border-[#E5E5E0] rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-100" />
          <textarea placeholder="Share your experience…" value={formText} onChange={e=>setFormText(e.target.value)} rows={3} className="w-full text-sm border border-[#E5E5E0] rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-100 resize-none" />
          <button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#7c3aed] hover:bg-violet-700 disabled:opacity-60 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors">
            {submitting?'Posting…':'Post Review'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AgentProfileClient({ profile }: { profile: AgentProfile }) {
  const alsoHire = profile.alsoHireSlugs.slice(0,3)

  return (
    <div className="min-h-screen bg-[#F2F1EE]">
      <Navbar />
      <div className="h-28 md:h-36 bg-[#0f0f0f] w-full" />
      <div className="max-w-5xl mx-auto px-3 md:px-4">
        {/* Profile header card */}
        <div className="bg-white border border-[#E5E5E0] rounded-2xl mb-5 md:-mt-6 px-4 md:px-6 pb-6 pt-0 relative">
          <div className="flex items-center justify-between">
            <div className="mt-4 md:-mt-14 mb-3">
              <div className="w-20 h-20 md:w-28 md:h-28 rounded-full p-1 ring-4 ring-white shadow-xl overflow-hidden bg-[#0f0f0f]">
                <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center bg-[#0f0f0f]">
                  <Image src="/eve-bot.png" alt={`AI ${profile.role}`} width={112} height={112} className="w-full h-full object-cover object-center" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap md:self-end md:mb-3">
              <Link href={`/login?redirect=/ai/${profile.slug}`} className="bg-[#0f0f0f] text-white font-semibold text-sm px-5 py-2 rounded-full transition-colors hover:bg-gray-800">Hire Now</Link>
              <button className="border border-[#0f0f0f] text-[#0f0f0f] font-semibold text-sm px-5 py-2 rounded-full transition-colors hover:bg-gray-50">Save</button>
            </div>
          </div>
          <h1 className="font-[Cormorant_Garamond] text-2xl md:text-3xl font-bold text-gray-900 leading-tight">AI {profile.role}</h1>
          <p className="text-gray-500 text-sm mt-1">{profile.department} · Autonomous · 24/7 · 50+ Languages</p>
          <p className="text-gray-400 text-xs mt-1">Preplix AI Marketplace · Remote — global</p>
          <div className="mt-5 flex flex-wrap gap-x-4 gap-y-3">
            {profile.stats.map(s=>(
              <div key={s.label} className="text-center">
                <p className="text-lg font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col md:flex-row gap-5 items-start pb-12">
          {/* Left */}
          <div className="w-full md:flex-1 min-w-0 space-y-5 order-2 md:order-1">
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
              <h2 className="font-[Cormorant_Garamond] text-2xl font-semibold text-gray-900 mb-3">About</h2>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{profile.about}</p>
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
              <h2 className="font-[Cormorant_Garamond] text-2xl font-semibold text-gray-900 mb-4">Core Competencies</h2>
              <div className="flex flex-wrap gap-2">
                {profile.competencies.map(c=>(
                  <span key={c} className="text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700">{c}</span>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
              <h2 className="font-[Cormorant_Garamond] text-2xl font-semibold text-gray-900 mb-4">How to Deploy</h2>
              <div className="space-y-4">
                {profile.deploySteps.map((step,i)=>(
                  <div key={i} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#0f0f0f] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i+1}</div>
                    <div><p className="font-semibold text-gray-900 text-sm">{step.title}</p><p className="text-sm text-gray-500 mt-0.5">{step.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
              <h2 className="font-[Cormorant_Garamond] text-2xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
              <div className="space-y-4">
                {profile.activity.map((a,i)=>(
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ActivityIcon icon={a.icon} />
                    </div>
                    <div><p className="text-sm text-gray-700 leading-relaxed">{a.text}</p><p className="text-xs text-gray-400 mt-1">{a.time}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
              <h2 className="font-[Cormorant_Garamond] text-2xl font-semibold text-gray-900 mb-4">Languages</h2>
              <div className="grid grid-cols-2 gap-2">
                {profile.languages.map(l=>(
                  <div key={l.lang} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="text-lg">{l.flag}</span><span>{l.lang}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500"><span>+45 more</span></div>
              </div>
            </div>
            <ReviewsSection slug={profile.slug} />
          </div>

          {/* Right sidebar */}
          <div className="w-full md:w-80 flex-shrink-0 space-y-5 md:sticky md:top-20 order-1 md:order-2">
            <ChatPanel profile={profile} />
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
              <h3 className="font-[Cormorant_Garamond] text-xl font-semibold text-gray-900 mb-3">Pricing</h3>
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-gray-900">$400<span className="text-lg font-normal text-gray-400">/mo</span></p>
                <p className="text-xs text-gray-400 mt-1">per AI employee · cancel anytime</p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-xl p-3 mb-4 text-center">
                <p className="text-xs text-gray-500">Human equivalent</p>
                <p className="text-sm font-semibold text-gray-700 line-through">$4,000+/mo</p>
                <p className="text-sm font-bold text-green-600 mt-0.5">You save $3,600+/mo</p>
              </div>
              <Link href="/login?redirect=/onboarding" className="block w-full bg-[#0f0f0f] hover:bg-gray-800 text-white font-semibold text-sm py-2.5 rounded-xl text-center transition-colors">
                Hire Now →
              </Link>
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
              <h3 className="font-semibold text-gray-900 text-sm mb-3">Deploy On</h3>
              <div className="grid grid-cols-2 gap-2">
                {[{icon:'💬',label:'WhatsApp'},{icon:'✈️',label:'Telegram'},{icon:'🌐',label:'Web Chat'},{icon:'📧',label:'Email'}].map(ch=>(
                  <div key={ch.label} className="flex items-center gap-1.5 text-sm text-gray-600 bg-gray-50 rounded-lg px-2.5 py-2">
                    <span>{ch.icon}</span><span className="text-xs">{ch.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {alsoHire.length > 0 && (
              <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
                <h3 className="font-semibold text-gray-900 text-sm mb-3">You May Also Hire</h3>
                <div className="space-y-3">
                  {alsoHire.map(slug=>(
                    <div key={slug} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <span className="font-bold text-xs text-gray-600">{slug.charAt(0).toUpperCase()}</span>
                        </div>
                        <p className="text-xs font-semibold text-gray-800 capitalize">{slug.replace(/-/g,' ')}</p>
                      </div>
                      <Link href={`/ai/${slug}`} className="text-xs font-medium flex-shrink-0 hover:underline text-gray-900">Hire →</Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
